<template>
  <div class="card main-cards statistics">
    <div class="stats-header">
      <img :src="icon" :alt="title" />
      <h3>{{ title }}</h3>
    </div>
    <p v-if="showDescription" class="stats-intro-p">
      {{ getCurrentTokenDisclaimer() }}
    </p>

    <div class="table">
      <el-table
        :data="getListItems()"
        :empty-text="$t('coreNoData')"
        style="width: 100%"
        :border="false"
        :table-layout="$device.isMobile ? 'auto' : 'fixed'"
        :header-cell-style="{ background: 'linear-gradient(180deg, #071821 0%, #040E13 100%) !important' }"
        class="activity-list"
      >
        <el-table-column prop="amount" :label="$t('dashboardPurchasesAmount')">
          <template #default="scope">
            <div class="with-icon">
              <img :src="`/img/icons/${token}.svg`" loading="lazy" alt="" width="32" />
              {{ $formatValue(scope.row.amount) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="claimed" :label="$t('dashboardUnlockedLabel')">
          <template #default="scope">
            {{ $formatValue(getClaimedValue(scope.row)) }}
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" :label="$t('dashboardPurchasesCreated')">
          <template #default="scope">
            {{ formatDate(scope.row.createdAt) }}
          </template>
        </el-table-column>

        <el-table-column v-if="address" prop="released" :label="$t('dashboardPurchasesAction')">
          <template #default="scope">
            <div v-if="scope.row.released === false && isUnlocked(scope.row.createdAt)">
              {{ getUnlockableValue(scope.row.stakeIdx) }}
            </div>
            <template v-else-if="Number(scope.row.amount) !== Number(scope.row?.claimed) && !scope.row.released">
              <div>{{ lockedTill(scope.row.createdAt) }}</div>
            </template>
            <template v-else>
              <div>{{ $t('dashboardPurchasesClaimed') }}</div>
            </template>
          </template>
        </el-table-column>
        <el-table-column v-if="address" prop="released" :label="$t('dashboardPurchasesUnlock')" align="left">
          <template #default="scope">
            <div>
              <UnstakeButton
                :token-address="token"
                :index="scope.row.stakeIdx"
                :disabled="
                  !(scope.row.released === false && isUnlocked(scope.row.createdAt)) ||
                  waitingStakeIdx === scope.row.stakeIdx
                "
                @update="updateHandler(scope.row.stakeIdx)"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!stakeData?.items || !stakeData?.items.length" class="controls">
        <button class="w-button btn-primary small" @click="openPurchaseTokens">
          {{ $t('dashboardBuyPearls') }}
        </button>
      </div>
      <div v-if="stakeData?.items && stakeData.items.length > SHORT_LIST_ITEMS_LENGTH" class="controls">
        <SwitcherButton
          :active-text="$t('appStatisticShowMore')"
          :inactive-text="$t('appStatisticShowLess')"
          :change="() => (isShowShortList = !isShowShortList)"
        />
      </div>
    </div>

    <el-dialog
      v-model="isPurchaseTokenOpen"
      class="crafting buyPearlModal"
      :fullscreen="$device.isMobile"
      :title="$props.title"
      :append-to-body="true"
      :center="true"
      :class="['without-header', { 'ru-locale': $i18n.locale === 'ru' }]"
      @close="store.openedBuilding = null"
    >
      <BuyPearlModal :token="token" :is-secondary="isSelectedTokenSecondary" />
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { StakeModel } from '~/types/apiService';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { SHORT_LIST_ITEMS_LENGTH, Tokens } from '~/utils/constants';
import useDashboardPurchases from '~/composables/useDashboardPurchases';

const props = defineProps<{
  token: Tokens;
  title: string;
  icon: string;
  showDescription?: boolean;
}>();

const store = useMainStore();
const { t } = useI18n();
const { address } = useWeb3ModalAccount();
const { waitingStakeIdx, stakeData, getUnlockableValue, lockedPeriodRef, updateAfterUnstake } = useDashboardPurchases(
  props.token
);

const emit = defineEmits(['unlocked']);

const isSelectedTokenSecondary = ref(false);
const isShowShortList = ref(true);
const isPurchaseTokenOpen = ref(false);

function openPurchaseTokens() {
  isSelectedTokenSecondary.value = props.token === Tokens.blackPearl ? true : false;
  isPurchaseTokenOpen.value = !isPurchaseTokenOpen.value;
}

function getCurrentTokenDisclaimer(): string {
  if (props.token === Tokens.pearl) return t('dashboardPurchasesPearlDisclaimer');
  else if (props.token === Tokens.blackPearl) return t('dashboardPurchasesBlackPearlDisclaimer');
  else return t('dashboardPurchasesGoldOreDisclaimer');
}

function isUnlocked(date: string) {
  const ts = +new Date(date) + Number(lockedPeriodRef.value) * 1000;
  return ts < +new Date();
}

function lockedTill(date: string) {
  const ts = +new Date(date) + Number(lockedPeriodRef.value) * 1000;
  return formatDate(ts);
}

function formatDate(value: string | number) {
  if (!value) {
    return '';
  }

  return new Date(value).toLocaleString('en-GB');
}

function getListItems(): StakeModel[] {
  if (isShowShortList.value && stakeData.value) {
    return stakeData.value.items.slice(0, SHORT_LIST_ITEMS_LENGTH);
  }

  return stakeData.value?.items || [];
}

function getClaimedValue(row: StakeModel): string {
  if (!row?.claimed) {
    return '0';
  }

  // Убеждаемся, что claimed не превышает amount
  const claimed = Number(row.claimed);
  const amount = Number(row.amount);

  if (claimed > amount) {
    // Если claimed превышает amount, возвращаем amount (максимально возможное значение)
    return String(amount);
  }

  return row.claimed;
}

async function updateHandler(stakeIdx: number) {
  await updateAfterUnstake(stakeIdx);
  emit('unlocked');
}
</script>

<style scoped lang="scss">
.controls {
  display: flex;
  justify-content: center;
  padding: 40px 10px 0;
  text-align: center;

  button {
    min-width: 250px;

    .token-name {
      text-transform: lowercase;
    }
  }

  @media screen and (max-width: 767px) {
    padding-top: 24px;
    padding-bottom: 24px;

    button {
      max-width: fit-content;
    }
  }
}

.table {
  @media screen and (max-width: 767px) {
    margin-left: -16px;
    margin-right: -16px;
  }

  @media screen and (max-width: 479px) {
    margin-left: -38px;
    margin-right: -38px;
    position: static;
    overflow: auto;
    background: var(--blue-3);
    border-top: 1px solid var(--blue-2);
    border-bottom: 1px solid var(--blue-2);
  }
}

.controls {
  display: flex;
  justify-content: center;
  margin-bottom: 0.8rem;
}

.card.main-cards.statistics {
  padding: 0px 16px;
  padding-bottom: 24px;
}

@media screen and (max-width: 479px) {
  .card.main-cards.statistics {
    margin-top: -16px;
    padding-bottom: 0;
  }
}

.with-icon {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;

  img {
    margin-left: -8px;
  }
}

.stats-header {
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 50px;
    width: 50px;
  }

  h3 {
    color: #fff;
    font-weight: 700;
    margin-left: 10px;
    font-family: Grenze;
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>

<style>
.purchases-table {
  .el-skeleton {
    height: 370px;
    margin-bottom: 24px;

    .el-skeleton__item {
      height: 370px;
      border-radius: 12px;
    }
  }
}
</style>
